import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import {
  CalendarBlank,
  Clock,
  User,
  CurrencyDollar,
  TrendUp,
  Warning,
} from "@phosphor-icons/react";
import { format, startOfWeek, endOfWeek, subWeeks, addWeeks } from "date-fns";

const LaborCostsPage = () => {
  const [weekAnchor, setWeekAnchor] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const weekStart = startOfWeek(weekAnchor, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(weekAnchor, { weekStartsOn: 1 });
  const startStr = format(weekStart, "yyyy-MM-dd");
  const endStr = format(weekEnd, "yyyy-MM-dd");

  useEffect(() => {
    fetchLaborCosts();
  }, [weekAnchor]);

  const fetchLaborCosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/timeclock/labor-cost?start_date=${startStr}&end_date=${endStr}`
      );
      setData(res.data);
    } catch (error) {
      toast.error("Failed to load labor costs");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const prevWeek = () => setWeekAnchor(subWeeks(weekAnchor, 1));
  const nextWeek = () => setWeekAnchor(addWeeks(weekAnchor, 1));
  const thisWeek = () => setWeekAnchor(new Date());

  const fmtCurrency = (v) =>
    v.toLocaleString("en-US", { style: "currency", currency: "USD" });

  return (
    <div className="min-h-screen bg-background" data-testid="labor-costs-page">
      <Sidebar />
      <main className="main-content p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-medium">Labor Costs</h1>
            <p className="text-muted-foreground">
              Weekly payroll projection based on hours worked
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={prevWeek}
              data-testid="labor-prev-week"
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={thisWeek}
              data-testid="labor-this-week"
            >
              This Week
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={nextWeek}
              data-testid="labor-next-week"
            >
              Next
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="gap-2"
                  data-testid="labor-date-picker"
                >
                  <CalendarBlank size={18} />
                  {format(weekStart, "MMM d")} – {format(weekEnd, "MMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={weekAnchor}
                  onSelect={(d) => d && setWeekAnchor(d)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data ? (
          <p className="text-center py-20 text-muted-foreground">
            No data available
          </p>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <CurrencyDollar
                      size={24}
                      className="text-primary"
                      weight="fill"
                    />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Labor Cost</p>
                    <p
                      className="font-mono text-2xl font-medium"
                      data-testid="labor-total-cost"
                    >
                      {fmtCurrency(data.total_labor_cost)}
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Clock size={24} className="text-blue-600" weight="fill" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Hours</p>
                    <p
                      className="font-mono text-2xl font-medium"
                      data-testid="labor-total-hours"
                    >
                      {data.total_hours.toFixed(1)}h
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Warning
                      size={24}
                      className="text-orange-600"
                      weight="fill"
                    />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Overtime Cost</p>
                    <p
                      className="font-mono text-2xl font-medium"
                      data-testid="labor-overtime-cost"
                    >
                      {fmtCurrency(data.total_overtime_cost)}
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <User size={24} className="text-green-600" weight="fill" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Employees</p>
                    <p
                      className="font-mono text-2xl font-medium"
                      data-testid="labor-employee-count"
                    >
                      {data.employee_count}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Employee Breakdown Table */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">
                  Employee Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.employees.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock size={48} className="mx-auto mb-2 opacity-30" />
                    <p>No hours recorded this week</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground">
                          <th className="py-2 pr-4 font-medium">Employee</th>
                          <th className="py-2 pr-4 font-medium">Role</th>
                          <th className="py-2 pr-4 font-medium text-right">
                            Rate
                          </th>
                          <th className="py-2 pr-4 font-medium text-right">
                            Reg Hrs
                          </th>
                          <th className="py-2 pr-4 font-medium text-right">
                            OT Hrs
                          </th>
                          <th className="py-2 pr-4 font-medium text-right">
                            Days
                          </th>
                          <th className="py-2 font-medium text-right">
                            Total Cost
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.employees.map((emp) => (
                          <tr
                            key={emp.user_id}
                            className="border-b border-border/50 hover:bg-secondary/20 transition-colors"
                            data-testid={`labor-emp-${emp.user_id}`}
                          >
                            <td className="py-3 pr-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                  <User
                                    size={16}
                                    className="text-primary"
                                    weight="bold"
                                  />
                                </div>
                                <span className="font-medium">
                                  {emp.user_name}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <Badge variant="outline" className="capitalize">
                                {emp.role}
                              </Badge>
                            </td>
                            <td className="py-3 pr-4 text-right font-mono">
                              {emp.hourly_rate > 0
                                ? fmtCurrency(emp.hourly_rate)
                                : <span className="text-muted-foreground italic">Not set</span>}
                            </td>
                            <td className="py-3 pr-4 text-right font-mono">
                              {emp.regular_hours.toFixed(1)}h
                            </td>
                            <td className="py-3 pr-4 text-right font-mono">
                              {emp.overtime_hours > 0 ? (
                                <span className="text-orange-600 font-medium">
                                  {emp.overtime_hours.toFixed(1)}h
                                </span>
                              ) : (
                                "0h"
                              )}
                            </td>
                            <td className="py-3 pr-4 text-right font-mono">
                              {emp.days_worked}
                            </td>
                            <td className="py-3 text-right font-mono font-medium">
                              {fmtCurrency(emp.total_cost)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-border font-medium">
                          <td className="py-3 pr-4" colSpan={3}>
                            Totals
                          </td>
                          <td className="py-3 pr-4 text-right font-mono">
                            {data.employees
                              .reduce((s, e) => s + e.regular_hours, 0)
                              .toFixed(1)}
                            h
                          </td>
                          <td className="py-3 pr-4 text-right font-mono">
                            {data.employees
                              .reduce((s, e) => s + e.overtime_hours, 0)
                              .toFixed(1)}
                            h
                          </td>
                          <td className="py-3 pr-4"></td>
                          <td
                            className="py-3 text-right font-mono text-lg"
                            data-testid="labor-table-total"
                          >
                            {fmtCurrency(data.total_labor_cost)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Missing Rate Warning */}
            {data.employees.some((e) => e.hourly_rate === 0) && (
              <div
                className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-start gap-3"
                data-testid="labor-rate-warning"
              >
                <Warning
                  size={20}
                  className="text-orange-600 mt-0.5 flex-shrink-0"
                  weight="fill"
                />
                <div>
                  <p className="font-medium text-orange-800">
                    Missing hourly rates
                  </p>
                  <p className="text-sm text-orange-700">
                    Some employees don't have an hourly rate set. Go to{" "}
                    <span className="font-medium">Settings &gt; Users</span> and
                    edit each employee to set their rate for accurate cost
                    projections.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default LaborCostsPage;
